import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTokenPriceWithUseAPI } from '~/hooks/useTokenPrice'
import { useSpotSwapPair } from '~/state/dex/hooks'
import Skeleton from '~/components/Skeleton'
import { StyledDetailWrapper } from '~/components/Exchange/styled'
import { formatUnits } from 'viem'
import { toBigNumber, formatFormAmount } from '~/utils/numbers'
import { Currency, FixedPointNumber } from '~/utils/fixedPoint'
import styled from 'styled-components'

interface Props {
  fee: bigint
  loading: boolean
}

const CrossTradeFee: React.FC<Props> = ({ fee, loading }) => {
  const { t } = useTranslation()
  const [, outputToken] = useSpotSwapPair()
  const tokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: outputToken?.address,
    chainId: outputToken?.chainId,
  })
  const formattedTradeFee = formatUnits(fee, outputToken?.decimals)
  const usdValue = toBigNumber(formattedTradeFee).times(tokenPrice)
  const isZeroTradeFee = formattedTradeFee === '0'

  const usdValueLiteral = useMemo(() => {
    if (!Number(usdValue)) {
      return '~'
    } else if (Number(usdValue) < 0.01) {
      return '< $0.01'
    } else {
      return `${new FixedPointNumber(usdValue).toCurrency(Currency.USD, 2)}`
    }
  }, [usdValue])

  const valueLiteral = isZeroTradeFee
    ? `0 ${outputToken?.symbol}`
    : Number(formattedTradeFee)
    ? `${usdValueLiteral}(${formatFormAmount(formattedTradeFee, 3)} ${
        outputToken?.symbol
      })`
    : '~'

  return (
    <StyledDetailWrapper>
      <span>{t('trade_fee')}</span>
      {loading ? <Skeleton /> : <StyledValue>{valueLiteral}</StyledValue>}
    </StyledDetailWrapper>
  )
}

export default CrossTradeFee

const StyledValue = styled.div`
  color: ${({ theme }) => theme.colors.textStrong};
  text-align: right;
`
